/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */

/**
 * Observes new iframes and executes the action on them.
 * @param {Window|Iframe} win
 * @param {MutationObserver} observer
 * @param {() => void} callback
 */
const observeIframes = (win, observer, callback) => {
    const contentDocument = win.contentDocument || win.document;

    // If the iframe is not loaded yet or has different domain, we can't access its contentDocument
    if (contentDocument) {
        contentDocument.querySelectorAll('iframe').forEach(callback);
        // disconnect is not necessary, because if the iframe is removed, the observer won't listen to it anymore
        observer.observe(contentDocument, { childList: true, subtree: true });
    }
};

/**
 * Creates a new MutationObserver that observes new iframes.
 * @param {() => void} callback
 * @returns {MutationObserver}
 */
export const createNewIframeObserver = callback =>
    new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            Array.from(mutation.addedNodes)
                .filter(({ tagName }) => tagName === 'IFRAME')
                .forEach(callback);
        });
    });

/**
 * Observes new iframes and executes the action on them.
 * @param {() => void} action
 * @returns {MutationObserver}
 */
export const runActionInIframesRecursively = action => {
    let observer;

    const callback = iframe => {
        action(iframe);
        iframe.addEventListener('load', () => {
            observeIframes(iframe, observer, callback);
        });
        observeIframes(iframe, observer, callback);
    };

    // Same observer is used to avoid duplicated listeners
    observer = createNewIframeObserver(callback);

    observeIframes(window, observer, callback);

    return observer;
};
